import { Anchor, Box, Button, Flex, Grid, Modal, Switch, Text, Textarea, Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconClock,
  IconExternalLink,
  IconPlayerPlay,
  IconPlayerStop,
  IconSettings,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import diffInDays from '../../helpers/diffInDays';
import flowHasLinkWrappingNode from '../../helpers/flowHasLinkWrappingNode';
import ConfirmationModal from '../Modals/ConfirmationModal';

const rescheduleChannelLabels = {
  email: ['email', 'emails'],
  textMessage: ['text message', 'text messages'],
  viberMessage: ['Viber message', 'Viber messages'],
  pushNotification: ['push notification', 'push notifications'],
  whatsappMessage: ['WhatsApp message', 'WhatsApp messages'],
  webhook: ['webhook', 'webhooks'],
};

const rescheduleExplanation =
  'could not be sent yet because the recipients reached their daily message limit. They were rescheduled for the next allowed time slot that is at least 24 hours in the future. The campaign stays in Running until these messages are sent or the reschedule limit is reached.';

const buildRescheduleTooltipText = (rescheduleStatus) => {
  const parts = [];
  // A partial payload must never throw during render - the list page has no error boundary.
  for (const [channel, pendingCount] of Object.entries(rescheduleStatus.pendingByChannel ?? {})) {
    if (!pendingCount) {
      continue;
    }
    const labels = rescheduleChannelLabels[channel] || [channel, channel];
    parts.push(`${pendingCount} ${pendingCount === 1 ? labels[0] : labels[1]}`);
  }
  if (parts.length === 0) {
    return `Some messages ${rescheduleExplanation}`;
  }
  return `${parts.join(', ')} ${rescheduleExplanation}`;
};

const CampaignStatus = ({ campaign, i, toggleCampaign, rescheduleStatus, isClickTrackingVerified }) => {
  const location = useLocation();
  const { domain } = useSelector((state) => state.domain);
  const [showAbortConfirmation, setShowAbortConfirmation] = useState(false);
  const [showLaunchConfirmation, setShowLaunchConfirmation] = useState(false);
  const [showWebhookInstructions, setShowWebhookInstructions] = useState();

  const confirmAbort = () => {
    toggleCampaign(i, false);
    setShowAbortConfirmation(false);
  };

  let currentDate;
  if (campaign.scheduledAt) {
    currentDate = new Date(campaign.scheduledAt);
  } else {
    currentDate = new Date();
  }

  const CURRENT_DATE = currentDate.toISOString().split('T')[0];

  let startQuietDate = new Date(`${CURRENT_DATE}T${domain.quietHoursStart}`);
  let endQuietDate = new Date(`${CURRENT_DATE}T${domain.quietHoursEnd}`);

  const dateToTimeString = (date) => {
    const dateInString = date.toTimeString();
    const time = dateInString.split(' ');
    const shortTime = time[0].slice(0, -3);
    return shortTime;
  };

  let currentDateTime = {
    hours: currentDate.getHours(),
    minutes: currentDate.getMinutes(),
  };

  let startQuietDateTime = {
    hours: startQuietDate.getHours(),
    minutes: startQuietDate.getMinutes(),
  };
  let endQuietDateTime = {
    hours: endQuietDate.getHours(),
    minutes: endQuietDate.getMinutes(),
  };

  const inQuietHours = () => {
    if (currentDateTime.hours > startQuietDateTime.hours && currentDateTime.hours < endQuietDateTime.hours) {
      return true;
    }

    if (currentDateTime.hours === startQuietDateTime.hours) {
      return currentDateTime.minutes >= startQuietDateTime.minutes;
    }

    if (currentDateTime.hours === endQuietDateTime.hours) {
      return currentDateTime.minutes < endQuietDateTime.minutes;
    }
    return false;
  };

  const launchCampaign = () => {
    toggleCampaign(i, !campaign.running);
    setShowLaunchConfirmation(false);
  };

  // A campaign whose flow never wraps a link (ads, webhooks, segment-only automations)
  // never touches the click-tracking host, so it stays gate-free even with no click
  // record - matching the backend job gate, which only calls
  // getTrackingUrl/getClickTrackingUrl for the channels in flowHasLinkWrappingNode. Only
  // an explicit false blocks: while the status is still loading it is undefined, and an
  // unverified guess must not stop anyone launching. Deactivating is never blocked.
  const activationBlocked = isClickTrackingVerified === false && flowHasLinkWrappingNode(campaign.flow);
  // The notice does not name the DNS record: the API also refuses sends when the click
  // host fails its health probe, and this list must not probe per row - so it sends the
  // merchant to the settings tab that shows the record and the host check together.
  const clickTrackingNotice = (
    <Anchor
      size="xs"
      c="red"
      fw={500}
      href={`#/accounts/${domain.accountId}/domains/${domain.id}/settings/messagingAndAds?tab=general`}
    >
      Click tracking not ready - check Click Tracking settings
    </Anchor>
  );
  // Only wrap the Launch button in the notice's Flex when there is a notice to show -
  // otherwise the unblocked (overwhelmingly common) case gets a layout it never had.
  const withClickTrackingNotice = (control) =>
    activationBlocked ? (
      <Flex align="center" justify="flex-end" gap="xs">
        {clickTrackingNotice}
        {control}
      </Flex>
    ) : (
      control
    );

  let status = '';
  if (campaign.mode === 'oneOff') {
    if (campaign.finishedAt && campaign.finishedAt !== null && new Date(campaign.finishedAt) < new Date()) {
      if (campaign.abortedAt) {
        status = (
          <Flex align="center" display="inline-flex" gap={4}>
            <IconPlayerStop size={20} />
            <Text component="span" visibleFrom="md">
              Aborted
            </Text>
          </Flex>
        );
      } else {
        status = (
          <Flex align="center" display="inline-flex" gap={4} c="success">
            <IconCircleCheck size={20} />
            <Text component="span" visibleFrom="md">
              Finished
            </Text>
          </Flex>
        );
      }
    } else if (campaign.running || (campaign.startedAt && campaign.finishedAt === null)) {
      if (campaign.startedAt) {
        status = (
          <Flex align="center" display="inline-flex" gap={4}>
            <IconSettings size={20} />
            <Text component="span" visibleFrom="md">
              Running{' '}
              {campaign.finishedAt && campaign.finishedAt !== null && (
                <Text component="span" c="dimmed">
                  ({diffInDays(new Date(), new Date(campaign.finishedAt))} days left)
                </Text>
              )}
            </Text>
            {rescheduleStatus?.total > 0 && (
              <Tooltip
                label={buildRescheduleTooltipText(rescheduleStatus)}
                multiline
                maw={260}
                withArrow
                events={{ hover: true, focus: true, touch: true }}
              >
                <Box
                  component="span"
                  style={{ display: 'inline-flex', cursor: 'help' }}
                  role="img"
                  aria-label="Some messages were rescheduled"
                  tabIndex={0}
                >
                  <IconAlertCircle size={20} color="var(--mantine-color-warning-6)" />
                </Box>
              </Tooltip>
            )}
          </Flex>
        );
      } else {
        status = (
          <Flex align="center" display="inline-flex" gap={4}>
            <IconClock size={20} />
            <Text component="span" visibleFrom="md">
              Scheduled
            </Text>
          </Flex>
        );
      }
      status = (
        <>
          <Button
            size="xs"
            color="red"
            mb="xs"
            mr="sm"
            onClick={() => setShowAbortConfirmation(true)}
            leftSection={<IconPlayerStop size={14} />}
          >
            Abort
          </Button>
          {status}
        </>
      );
    } else if (campaign.isAiEmail && campaign.aiEmailsStatus === 'generating') {
      status = (
        <Flex align="center" display="inline-flex" gap={4}>
          <IconSettings size={20} />
          <Text component="span" visibleFrom="md">
            Generating
          </Text>
        </Flex>
      );
    } else {
      status = withClickTrackingNotice(
        <Button
          size="xs"
          disabled={activationBlocked}
          onClick={
            !campaign.useOptimalTime && campaign.mode !== 'oneOff' && inQuietHours(currentDate)
              ? () => setShowLaunchConfirmation(true)
              : launchCampaign
          }
          rightSection={<IconPlayerPlay size={14} />}
        >
          <span className="mantine-visible-from-md">Launch</span>
        </Button>
      );
    }
  } else if (campaign.mode === 'continuous' || campaign.mode === 'triggered') {
    const isExpired = campaign.endsAt && new Date(campaign.endsAt) < new Date();
    if (isExpired) {
      status = (
        <Text fw={700} c="red">
          EXPIRED
        </Text>
      );
    } else if (
      campaign.mode === 'continuous' &&
      campaign.scheduledAt &&
      !campaign.running &&
      new Date(campaign.scheduledAt) > new Date()
    ) {
      status = withClickTrackingNotice(
        <Button
          size="xs"
          disabled={activationBlocked}
          onClick={launchCampaign}
          rightSection={<IconPlayerPlay size={14} />}
        >
          <span className="mantine-visible-from-md">Launch</span>
        </Button>
      );
    } else {
      const isScheduledAndWaiting =
        campaign.mode === 'continuous' &&
        campaign.running &&
        campaign.scheduledAt &&
        new Date(campaign.scheduledAt) > new Date();
      status = (
        <Flex align="center" justify="flex-end" gap="xs">
          {campaign.trigger?.type === 'webhook' && (
            <Anchor component="button" c="dimmed" onClick={() => setShowWebhookInstructions(true)}>
              webhook instructions
            </Anchor>
          )}
          {isScheduledAndWaiting && (
            <Flex align="center" display="inline-flex" gap={4}>
              <IconClock size={20} />
              <Text component="span" visibleFrom="md">
                Scheduled
              </Text>
            </Flex>
          )}
          {campaign.mode === 'continuous' && campaign.endsAt && new Date(campaign.endsAt) > new Date() && (
            <Flex align="center" display="inline-flex" gap={4}>
              <IconClock size={20} />
              <Text component="span" visibleFrom="md">
                Ends {new Date(campaign.endsAt).toLocaleDateString()}
              </Text>
            </Flex>
          )}
          {activationBlocked && !campaign.running && clickTrackingNotice}
          <Switch
            size="lg"
            disabled={activationBlocked && !campaign.running}
            defaultChecked={campaign.running}
            onChange={(e) => toggleCampaign(i, e.target.checked)}
          />
        </Flex>
      );
    }
  }

  if (campaign.isAiEmail && campaign.aiEmailsStatus === 'finished') {
    const href = location.pathname.replace('/campaigns', `/emails?campaignId=${campaign.id}&tab=aiGeneratedEmails`);
    status = (
      <>
        <Button component="a" size="xs" mr="xs" mb="xs" href={'#' + href}>
          View Generated Emails
        </Button>
        {status}
      </>
    );
  }

  return (
    <>
      {status}
      <ConfirmationModal
        show={showAbortConfirmation}
        message="If you abort now, you will not be able to resume it later."
        close={() => setShowAbortConfirmation(false)}
        confirm={confirmAbort}
      />
      <ConfirmationModal
        show={showLaunchConfirmation}
        message={`If you start this campaign now, emails will not send until the shop is outside quiet hours (${dateToTimeString(
          startQuietDate
        )} - ${dateToTimeString(endQuietDate)}).`}
        close={() => setShowLaunchConfirmation(false)}
        confirm={launchCampaign}
      />

      <Modal
        opened={showWebhookInstructions}
        onClose={() => setShowWebhookInstructions(false)}
        title={`Webhook Instructions - ${campaign.name}`}
      >
        <Grid mb="md">
          <Grid.Col span={12}>
            <Textarea
              rows={3}
              value={`curl -XPOST H 'content-type: application/json' -H 'authorization: Bearer ${domain.secretKey}' ${
                import.meta.env.VITE_ROOT
              }/api/v0/campaigns/${campaign.id}/runs -d'{"trigger": {"custom": {}}}'`}
              disabled
            />
            <Text size="xs" c="dimmed" mt="xs">
              <p>
                The example above illustrates how to trigger this campaign with a webhook using curl, although any HTTP
                client will do.
              </p>
              <p>
                Keep in mind that each webhook call will trigger the campaign, and profiles belonging to the segment
                will enter the campaign as long as their cooldown period for this campaign has elapsed.
              </p>
              <p>
                You may pass{' '}
                <Anchor
                  href="https://releva.ai/documentation/developer-documentation/server-side-integration/#articleTOC_7"
                  rel="noopener"
                >
                  custom fields <IconExternalLink size={12} />
                </Anchor>{' '}
                and then use their values as merge tags in your messages (e.g. {'{{custom.string.foo}}'})
              </p>
            </Text>
          </Grid.Col>
        </Grid>
        <div style={{ textAlign: 'right' }}>
          <Button variant="default" onClick={() => setShowWebhookInstructions(false)}>
            Ok, got it!
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default CampaignStatus;
